import { isDraftColumnId } from '@/cascade/types';
import type { SidebarTabItem } from '@helpers/types/SidebarTabItem';
import { OfficeLocationSidebarViewEnum } from '@components/OfficeLocations/OfficeLocationSidebarViewEnum';
import { StaffFileSidebarViewEnum } from '@components/StaffFile/StaffFileSidebarViewEnum';
import { PatientFileMark } from '@/const/PatientFileMarks';
import {
  AppointmentResponseDto,
  ConnectedStripeAccountFullSizeDto,
  ExerciseFullSizeDto,
  TreatmentPlanResponseDto,
  TreatmentResponseDto,
  UserFullSizeDto,
  Visibility,
} from '@therame/shared';
import { Dayjs } from 'dayjs';

export type SidebarTypeMap = {
  exerciseDetails: {
    props: {
      exerciseId: string;
      readonly?: boolean;
    };
    sidebarResult: undefined;
    backButtonActive: boolean;
    sidebarTabItems: SidebarTabItem[];
  };
  exerciseEdit: {
    props: {
      // Scalar id (idProp) — reconstructable from the URL cascade segment. The
      // panel self-fetches the exercise by id (exercisesQueries.getById), so it
      // hydrates on a cold inline deep-link and docks under `exerciseDetails`.
      exerciseId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  addOfficeLocation: {
    props: {
      /** Cascade column identity only (the `new` sentinel); see createPatient. */
      officeLocationDraftId?: string;
    } | null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  customFormDetail: {
    props: {
      // Either a real CustomForm id (edit mode) or a create-column generation id
      // (`new`, `new~2`, …) for the create flow. A saved create CLOSES its panel: it
      // used to swap the URL `:new` → real id, which a draft parked in the zone has no
      // column to do (ADR-0029 §G), so both homes now close (#1111/#1120).
      customFormId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  previewCustomForm: {
    props: {
      // Cascade column identity only: reuses the parent editor's form id so the
      // preview docks as a child column. The component reads the live editor draft
      // (resolved from the editor registry), not this id (#1111).
      customFormId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  exerciseProgramStatistic: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  exerciseProgramDetail: {
    props: {
      exerciseProgramId: string;
      day: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  prescription: {
    props: {
      // Either a real TreatmentPlan id (edit mode) or a create-column generation id
      // (`new`, `new~2`, …) for the create flow. The panel self-fetches in edit mode;
      // in create mode the picker → form sequence lives in a draft slot so parked
      // drafts keep their own step, and a save closes the panel (ADR-0029 §G). RS-006.
      prescriptionId: string;
      initialPatientId?: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  patientInvoiceDetail: {
    props: {
      invoiceId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  datevExportHistory: {
    props: {
      officeLocationId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  gkvBillingRun: {
    props: {
      // The §302 Kandidatenansicht is cut on the OfficeLocation and never on the
      // Leistungserbringer (#1511): the Rechnungsart varies from file to file
      // within one run, and a per-Leistungserbringer cut would quietly make it
      // constant. So the column id is the location, which is also what makes it
      // a scalar the URL can carry on a cold deep-link.
      officeLocationId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  gkvBillingRunHistory: {
    props: {
      officeLocationId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  gkvBillingRunDetail: {
    props: {
      gkvBillingRunId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  dunningRun: {
    props: {
      officeId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  dunningRunHistory: {
    props: {
      officeId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  dunningRunRun: {
    props: {
      dunningRunId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  invoiceRun: {
    props: {
      officeLocationId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  appointmentDetails: {
    props: {
      appointmentId?: string;
      selectedDayInit?: Dayjs | null;
      selectedDurationInit?: number | null;
      selectedPatientsInit?: UserFullSizeDto[] | null;
      selectedTherapistInit?: UserFullSizeDto | null;
    };
    sidebarResult: {
      saveType: 'single';
      appointment?: AppointmentResponseDto;
    };
    sidebarTabItems: SidebarTabItem[];
  };
  appointment: {
    props: {
      // Scalar id (idProp) — reconstructable from the URL cascade segment. The
      // column self-fetches the appointment, so it hydrates on a cold deep-link.
      appointmentId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  appointmentPlanning: {
    props: {
      // Scalar id (idProp) — reconstructable from the URL cascade segment. The
      // column self-fetches the Verordnung, so it hydrates on a cold deep-link.
      treatmentPlanId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  appointmentComposer: {
    props: {
      initialVideoCall?: boolean;
      /**
       * Cascade column identity only (the `new` sentinel). The composer is
       * create-only and ignores its value — it exists so a prefill-free compose
       * can live as a durable, URL-serialized cascade column on a workspace
       * route (the calendar Termin-erstellen button). A prefilled open omits it
       * and stays an overlay, since the `type:id` URL can't carry the pre-fill.
       */

      composerId?: string;
      /** Pre-select the mode card when the sidebar opens (defaults to 'time'). */
      initialMode?: 'time' | 'slot';
      /** Pre-fill state.behandlungsplan + state.patient (used by TP-driven entry points). */
      initialTreatmentPlan?: TreatmentPlanResponseDto | null;
      /**
       * Pre-fill the treatment plan by id (ADR-0027 §A create-Column path). When the
       * composer docks as a `:new` column under a `prescription`, the
       * `parentIds` binding writes the parent's `prescriptionId` here; the composer
       * resolves it against `treatmentPlansQueries.byId` so the plan seeds even on a
       * cold inline deep-link. `initialTreatmentPlan` (the full DTO) still wins warm.
       */
      initialTreatmentPlanId?: string | null;
      /**
       * Pre-fill the Patient (used by entry points that already know it: patient file,
       * pending-assignment pill, active video call). Mirrors `appointmentDetails`'
       * `selectedPatientsInit` — the composer is single-patient, so callers pass one.
       */
      initialPatient?: UserFullSizeDto | null;
      /**
       * Pre-fill the Patient by id (ADR-0027 §A create-Column path): when the
       * composer opens as a `:new` cascade column under a `patientFile`, the only
       * prefill is the patient, and it is recoverable from the parent column's
       * `patientId` via the `parentIds` binding. Resolved against `usersQueries`
       * at open — mirrors `initialTherapistId` — so the patient seeds even on a cold
       * inline deep-link. `initialPatient` (the full DTO) still wins on the warm
       * overlay path.
       */
      initialPatientId?: string | null;
      /** Pre-fill the Therapeut (used by calendar drag-to-create on a staff column). */
      initialTherapist?: UserFullSizeDto | null;
      /**
       * Pre-fill the Therapeut by therapistId (popover-deepen path, PRD #944 /
       * RS-006): the popover only knows the column therapistId, not the full
       * user — resolved against the composer's staff query at open.
       */
      initialTherapistId?: string | null;
      /** Pre-fill the Zeit-mode start date+time, e.g. from a calendar drag selection. */
      initialDay?: Dayjs | null;
      /** Pre-fill the manual per-appointment duration in minutes (calendar drag). */
      initialDurationMin?: number | null;
      /**
       * Pre-fill the single Leistung from the popover-deepen path (PRD #944 /
       * RS-006): seeded onto the first Heilmittel row.
       */
      initialTreatment?: TreatmentResponseDto | null;
      /** Pre-select event type (popover-deepen path, PRD #944 / RS-006). */
      initialEventType?: 'patient' | 'internal' | null;
      /** Pre-fill internal-event title (popover-deepen path, PRD #944 / RS-006). */
      initialInternalEventTitle?: string | null;
      /**
       * Anchor-mode deepen (PRD #944 / RS-007): the popover-placed slot the
       * composer's slot-finder schedules the remaining N − 1 units around.
       * When non-null, the composer opens in slot mode and the Zeit-mode
       * override from `initialDay` is suppressed.
       */
      initialAnchor?: {
        treatmentId: string;
        start: Date;
        end: Date;
        therapistId: string;
        duration: number;
      } | null;
    };
    sidebarResult: undefined;
  };
  createPatient: {
    props: {
      /**
       * Cascade column identity only (the `new` sentinel). The sidebar is
       * create-only and ignores its value — it exists so a prefill-free create
       * can live as a durable, URL-serialized cascade column on a workspace route
       * (the patient list "Patient anlegen" button). A prefilled open omits it
       * and stays an overlay, since the `type:id` URL can't carry the pre-fill.
       */
      patientDraftId?: string;
      /**
       * Optional raw text to pre-fill the patient name field. Used by the
       * patient search's no-match action (RS-005 / #949; the appointment panel since #1899). Per PRD #944
       * the raw string is dumped into the name field with NO phone/name parsing.
       */
      initialName?: string;
      /**
       * The create column this one was opened from — bound from the URL when it
       * docks under a composer, Verordnung, basket or "Übung erstellen" column (see
       * `parentIds`). The picker that opened it listens for answers by this origin.
       */
      openedFromId?: string;
    } | null;
    sidebarResult: {
      patientId: string;
    };
  };
  inviteUser: {
    props: {
      /** Cascade column identity only (the `new` sentinel); see createPatient. */
      staffDraftId?: string;
      hideRole?: boolean;
    } | null;
    sidebarResult: {
      email: string;
    };
  };
  basket: {
    props: {
      /**
       * Cascade column identity only (the `new` sentinel). The basket is a
       * session-scoped working set (no record id), so this is a column-identity
       * token; the panel reads its content from `exerciseBasketStore`. Library
       * openers pass it so "Übungen zuweisen" lives as a cascade column on the
       * Übungsbibliothek workspace; contextual openers (patient-file program
       * replace) omit it and stay overlays.
       */
      basketDraftId?: string;
    } | null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  template: {
    props: {
      exerciseTemplateId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  addExercise: {
    props: {
      startWithVisibility?: Visibility;
      startWithPatientId?: string;
      startWithPatientName?: string;
      lockPatientSelect?: boolean;
      fromQrCode?: boolean;
      /**
       * Cascade column identity only (the `new` sentinel). addExercise is
       * create-only and ignores its value — it exists so a prefill-free "Übung
       * erstellen" can live as a durable, URL-serialized cascade column on the
       * Übungsbibliothek workspace route. A prefilled open (patient/visibility)
       * omits it and stays an overlay, since the `type:id` URL can't carry the
       * pre-fill.
       */
      addExerciseDraftId?: string;
      /**
       * The program being edited, when opened from "Übung zum Programm hinzufügen"
       * in the patient file — bound from that parent column. The panel resolves the
       * program's patient from it and locks the patient step to them. That open
       * uses the program id as `addExerciseDraftId`: a record id, not the sentinel,
       * so it is a plain column rather than an Entwurf (the create-flow buffer is
       * one per type, shared with the library page's "Übung erstellen").
       */
      programId?: string;
    };
    sidebarResult: {
      sharedWithPatientId?: string;
      exercise?: ExerciseFullSizeDto;
    };
    sidebarTabItems: SidebarTabItem[];
  };
  staffFile: {
    props: {
      // The staff member's user id. In a columnar workspace this is the
      // cascade column id serialized into the URL; the panel self-fetches the
      // user by id (matching the patientFile pattern).
      staffId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  officeAchievements: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  officeLocationSettings: {
    props: {
      officeLocationId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  feedback: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  patientFilePreview: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  patientFile: {
    props: {
      patientId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  exerciseLibrary: {
    props: {
      // The program being edited. Cascade column identity only: the panel reads
      // nothing from it — its picks live in `exerciseBasketStore.temporaryBasket`,
      // seeded by the ItemsEditor that opens it.
      programId: string;
    } | null;
    // Picks go straight into the editor's draft. Only the narrow-screen "Abbrechen"
    // hands something back: the ask to undo every pick made since opening.
    sidebarResult: { discardPicks: boolean } | undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  library: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  officeProductDetails: {
    props: {
      // Scalar id (idProp) — reconstructable from the URL cascade segment. Either
      // a real OfficeProduct id (edit) or the cascade `NEW_ID_SENTINEL` ('new')
      // for the draft-column create flow.
      officeProductId?: string;
      // Warm-open convenience only: the cascade segment carries just the id, so
      // the panel self-fetches the Stripe account and hydrates on a cold inline
      // deep-link when this is absent.
      connectedStripeAccount?: ConnectedStripeAccountFullSizeDto;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  officeShopAccount: {
    // No props: the panel self-fetches the Stripe account so the tax slider stays
    // live across a rate change.
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  webhookSubscriptionDetails: {
    props: {
      webhookSubscriptionId?: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  webhookSubscriptionLogs: {
    props: {
      webhookSubscriptionId?: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  versandLieferungDetail: {
    props: {
      lieferungId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  webhookLogEntryDetails: {
    props: {
      webhookLogEntryId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  productDetails: {
    props: {
      officeProductId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  featureRanking: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  apiKeyDetails: {
    props: {
      apiKeyId?: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  exerciseExecutionPlayerSettings: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  anamnesisResults: {
    props: {
      // Scalar id (idProp) — reconstructable from the URL cascade segment. The
      // panel self-fetches the answer (with its form + element answers) by id,
      // so it hydrates on a cold inline deep-link; title/meta are derived from
      // the fetched answer.
      customFormAnswerId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  customFormPlayer: {
    props: {
      // Scalar id (idProp) — reconstructable from the URL cascade segment. The
      // panel self-fetches the form by id, so it hydrates on a cold inline
      // deep-link. `patientId` is a harmless extra attr when docked under
      // `patientFile` (parentIds binding) — on the patient's own /diagnosis
      // page there is no `patientFile` ancestor, so `inline` never fires there
      // (that route isn't a workspace one) and this always opens as an
      // overlay instead.
      customFormId: string;
      patientId?: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  programDetail: {
    props: {
      // Scalar id (idProp) — reconstructable from the URL cascade segment. The
      // panel self-fetches the program by id, so it works on a cold inline
      // deep-link. `programIndex`/`title` are display-only header seeds overlay
      // callers may still pass; absent on deep-link, where the header falls
      // back to the fetched program's name.
      programId: string;
      programIndex?: number;
      title?: string;
    };
    sidebarResult: { deleted: boolean };
    sidebarTabItems: SidebarTabItem[];
  };
  priceListDraftEditor: {
    props: {
      priceListId: string;
      officeLocationId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  priceListDetails: {
    props: {
      priceListId: string;
      officeLocationId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  priceListItemEdit: {
    props: {
      priceListId: string;
      officeLocationId: string;
      itemId: string;
      // Optional seed data: when missing the panel fetches via priceListsQueries.
      // Inline cascade callers pass only ids; overlay callers pass row seed to
      // avoid a load flicker.
      posNumber?: string;
      officialName?: string;
      customName?: string | null;
      priceNet?: number;
      priceGross?: number;
      copayment?: number | null;
      copaymentExempt?: boolean;
      isReadonly?: boolean;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  patientNewEpisode: {
    props: {
      userId: string;
      onBooked: () => Promise<unknown> | void;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  videoCallInfo: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  settingAccounts: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  officeProfile: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  settingNotifications: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  aiDictation: {
    props: {
      // The column id: the dictation, from the upload onward (TM-9652). The
      // recording itself is the dictation session (top bar), because the audio
      // lives in memory until it is uploaded.
      dictationId: string;
      // Bound from the `patientFile` ancestor inline; an overlay opener passes it.
      patientId?: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  aiTranscript: {
    props: {
      // The dictation whose transcript is shown beside its review (TM-9652).
      dictationId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  aiDocumentationProvenance: {
    props: {
      // The saved AI_ASSISTED entry whose origin is shown (TM-9594, column since TM-9652).
      itemId: string;
      patientId?: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  patientNotificationSettings: {
    props: {
      // The patient whose notification settings are edited — also the column id,
      // so the column round-trips through the URL unaided.
      patientId: string;
    };
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  help: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  settingsLegal: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
  settingsApiKeys: {
    props: null;
    sidebarResult: undefined;
    sidebarTabItems: SidebarTabItem[];
  };
};

export type SidebarType = keyof SidebarTypeMap;

export interface InlineCapability {
  // Which prop on the sidebar's props bag carries the id (and is reconstructed
  // from the URL cascade segment for the inverse direction).
  idProp: string;
  // Default tab view to activate when this column is opened inline; required to
  // make tab-driven panels render meaningful content on cold deep-link.
  defaultTabView?: string;
  // Ancestor lookup chain. For each entry, the id of the cascade column whose
  // `type` matches is bound to `idProp` on this column's props. The first entry
  // is also used as the immediate-parent type for cascade insertion: drilling
  // into this type from anywhere lands as a child of the first matching parent
  // column (replacing any tail past it).
  parentIds?: { type: SidebarType; idProp: string }[];
  // When this column has a child mounted in the cascade, the parent column
  // (i.e. the column declaring this entry) is forced onto this tab view so the
  // lineage reads coherently on deep-link.
  parentTab?: string;
  // This column is a VIEW OF its parent rather than a record of its own — the form
  // preview beside the form editor. Minimizing the parent closes it, because a
  // 500px panel left beside a 52px rail is an orphan pointing at something the user
  // just put away. Closing the parent already takes it (closeSubtree); this covers
  // the other way a parent stops being on screen. The draft zone applies the same
  // rule to its own children (`DraftInstance.child`).
  closesWithParent?: boolean;
  // No arrival ring when a sibling pick swaps this column's record in place. For a
  // column the user switches through constantly (one program after another), the
  // pulse reads as the column closing and reopening rather than as its content
  // changing. Re-trigger pulses (rule D) still ring.
  noSwapRing?: boolean;
}

export interface SidebarComponent {
  type: SidebarType;
  title: string;
  component: () => any;
  hideButtons?: boolean;
  noPadding?: boolean;
  hideBottomCloseButton?: boolean;
  closeWithClickOutside?: boolean;
  hasScrollToBottomButton?: boolean;
  position: 'bottom' | 'right';
  // When set, the type can render as a column in a columnar workspace host
  // (PRD #963). Absence means overlay-only.
  inline?: InlineCapability;
  // When true, the type always renders as an overlay regardless of host —
  // filters, confirm/alert dialogs, transient pickers, composers.
  alwaysOverlay?: boolean;
  // When true, the overlay renders above an anchored appointment peek (the
  // scrimless appointment panel peek, z-55) rather than the default overlay
  // band (z-45). Set for sidebars that can be opened stacked ON TOP of a peek —
  // createPatient / inviteUser from the patient search's no-match action
  // (RS-005 / #949) — so they don't render behind their own originator. Kept
  // below the modal layer (z-60) so a confirm/alert dialog still covers them.
  //
  // Only peeks that were ALREADY open stay below: one raised from INSIDE the
  // panel (its own discard prompt) is lifted to z-58 so it isn't buried by the
  // panel that asked it — `sidebarStore.elevatedOverlayOpen` and `GenericModal`'s
  // `aboveElevatedSidebar`. Both directions hold because each layer keeps the
  // height it opened at.
  elevated?: boolean;
  // Opens narrow like every column (500px, 375px on a compact page), unless the user has
  // widened this type before — the choice is kept per type (`cascadeStore`'s widened
  // types). A header toggle widens it to 1000px and folds it back (TM-9664). Only for
  // a panel that needs the room — the Verordnung reproduces the paper form's layout.
  // Narrow viewports always get the narrow layout. `'create'`: only the type's `:new` side can be widened.
  wide?: boolean | 'create';
}

export const sidebars: SidebarComponent[] = [
  {
    type: 'exerciseDetails', //
    title: 'Übungsdetails',
    component: () => import('@components/ExerciseLibrary/ExerciseDetailModal/ExerciseDetail.vue'),
    noPadding: true,
    hideButtons: true,
    closeWithClickOutside: true,
    position: 'right',
    // Patient cascade: when drilled into from a program's exercise list, dock as
    // a child column to the RIGHT of the open `programDetail` rather than replacing
    // the stack at root. The nearest-ancestor scan in `routeToCascade` only docks
    // when a matching parent is present, so the Übungsbibliothek workspace (which
    // has no programDetail column) keeps opening the exercise at root as before.
    // Drilled into from the "Übung zum Programm hinzufügen" column, it docks under
    // that column instead (the deeper match), so the library stays open beside it.
    inline: {
      idProp: 'exerciseId',
      parentIds: [
        { type: 'programDetail', idProp: 'programId' },
        { type: 'exerciseLibrary', idProp: '_libraryProgramId' },
      ],
    },
  },
  {
    type: 'exerciseEdit',
    title: 'Übung bearbeiten',
    component: () =>
      import('@components/ExerciseLibrary/ExerciseDetailModal/ExerciseEditSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Übungsbibliothek workspace (ADR-0027 §A): edit-of-a-record is a Column,
    // like a saved `prescription` under `patientFile`. Docks under the
    // `exerciseDetails` column it is opened from (same `exerciseId`). The panel
    // self-fetches by id, so it hydrates on a cold deep-link; off a workspace
    // route it stays an overlay.
    inline: {
      idProp: 'exerciseId',
      parentIds: [{ type: 'exerciseDetails', idProp: 'exerciseId' }],
    },
  },
  {
    type: 'officeLocationSettings', //
    title: 'Standort-Einstellungen',
    component: () => import('@components/OfficeLocations/OfficeLocationSettings.vue'),
    noPadding: false,
    hideButtons: false,
    closeWithClickOutside: true,
    position: 'right',
    inline: {
      idProp: 'officeLocationId',
      defaultTabView: OfficeLocationSidebarViewEnum.PROFIL,
    },
  },
  {
    type: 'prescription',
    // The Verordnung itself, written or read (TM-9664): a saved plan opens straight
    // on it, so it is no longer only the create form.
    title: 'Verordnung',
    component: () => import('@components/TreatmentPlan/TreatmentPlanSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    wide: true,
    // Two possible parents (TM-9664 — the plan-detail column it used to dock under
    // is gone; a saved plan opens straight on this one):
    // - `patientFile` (RS-007): a plan opened or a `:new` draft started from inside a
    //   patient. `initialPatientId` is a real prop of the create form; binding it to
    //   patientFile.patientId seeds the patient even on a cold deep-link. An edit
    //   ignores it.
    // - `gkvBillingRun` (§302 Kandidatenansicht, #1517): a Fall in the
    //   Kandidatenbaum *is* a plan, and the one thing an operator wants from a
    //   skipped row is to see why — so it opens as a sibling column beside the run
    //   rather than re-rooting the workspace and throwing the run away.
    // ⚠ `parentTab` stays PatientFileMark.GENERAL: it only applies on the
    // `reRootUnder` path, which is a patientFile move and never a run.
    inline: {
      idProp: 'prescriptionId',
      parentIds: [
        { type: 'patientFile', idProp: 'initialPatientId' },
        { type: 'gkvBillingRun', idProp: '_referrerOfficeLocationId' },
      ],
      parentTab: PatientFileMark.GENERAL,
    },
  },
  {
    type: 'patientInvoiceDetail',
    title: 'Rechnungsdetails',
    component: () => import('@components/PatientInvoice/PatientInvoiceColumnPanel.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Patient cascade (RS-007): docks under patientFile and pins it to GENERAL
    // (where the invoice list lives). The panel already self-fetches by
    // `invoiceId`, so no prop reshape is needed. Also docks under `prescription`
    // for the "open an invoice from a Verordnung's billing lane" drill off the
    // global Verordnungen list — where the Verordnung column is the root and the
    // invoice opens as its child. The parentIds idProp bindings (`patientId`,
    // `_referrerTreatmentPlanId`) are harmless extra attrs the panel ignores.
    inline: {
      idProp: 'invoiceId',
      parentIds: [
        { type: 'patientFile', idProp: 'patientId' },
        { type: 'prescription', idProp: '_referrerTreatmentPlanId' },
      ],
      parentTab: PatientFileMark.GENERAL,
    },
  },
  {
    type: 'datevExportHistory',
    title: 'DATEV-Export',
    component: () => import('@components/DatevExport/DatevExportHistorySidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'gkvBillingRun',
    title: 'GKV-Abrechnung (§ 302)',
    component: () => import('@components/GkvBillingRun/GkvBillingRunSidebar.vue'),
    // ⚠ `closeWithClickOutside` und `noPadding` sind an einer Spalte nicht tot:
    // `usePreferSidebars` ist ein lebender Per-Gerät-Schalter, und wer ihn
    // umlegt, bekommt diesen Panel über `GenericSidebar` als Overlay. Das ist
    // der eine Ort, an dem beide Felder gelesen werden. `noPadding`, weil der
    // Baum sein `p-2.5` selbst mitbringt.
    closeWithClickOutside: true,
    noPadding: true,
    position: 'right',
    // ⚠ A cascade COLUMN, not an overlay Sidebar — the one place this panel
    // deliberately parts company with Mahnlauf and Rechnungslauf beside it
    // (#1517). Three reasons, and none of them is taste:
    //  - ADR-0021 is replacing the overlay form; new surface does not join the
    //    stack it is retiring.
    //  - A column is full width, and this panel has a Balken, Empfängerzeilen,
    //    two levels of nesting and sums at every one of them. The Mahnlauf has a
    //    flat invoice list.
    //  - A column serializes into the URL, so the run itself comes back on a
    //    cold deep-link — an overlay is gone. ⚠ What comes back is the column,
    //    not the review: the Stichtag and the Abwahl live in component state and
    //    reset. Serializing those would mean inventing grammar the cascade does
    //    not have (`type:id[@tab]`), and #1517 asks for the column state.
    // No `parentIds`: the run is opened from the Rechnungen list itself, so it
    // is always the cascade root of its workspace.
    inline: {
      idProp: 'officeLocationId',
    },
  },
  {
    type: 'gkvBillingRunHistory',
    title: 'Vergangene Abrechnungsläufe',
    component: () => import('@components/GkvBillingRun/GkvBillingRunHistorySidebar.vue'),
    closeWithClickOutside: true,
    noPadding: true,
    position: 'right',
    // Sibling column of the run, not its replacement: whoever opens the history
    // wants to go back to the candidates they clicked out of.
    inline: {
      idProp: 'officeLocationId',
      parentIds: [{ type: 'gkvBillingRun', idProp: 'officeLocationId' }],
    },
  },
  {
    type: 'gkvBillingRunDetail',
    title: 'Abrechnungslauf',
    component: () => import('@components/GkvBillingRun/GkvBillingRunDetailSidebar.vue'),
    closeWithClickOutside: true,
    noPadding: true,
    position: 'right',
    // Under the history, because that is the only path here: the running run is
    // shown in the run's own column, not here.
    inline: {
      idProp: 'gkvBillingRunId',
      parentIds: [{ type: 'gkvBillingRunHistory', idProp: '_referrerOfficeLocationId' }],
    },
  },
  {
    type: 'dunningRun',
    title: 'Mahnwesen',
    component: () => import('@components/DunningRun/DunningRunSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'dunningRunHistory',
    title: 'Mahnlauf-Verlauf',
    component: () => import('@components/DunningRun/DunningRunHistorySidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'dunningRunRun',
    title: 'Mahnlauf',
    component: () => import('@components/DunningRun/DunningRunRunSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'invoiceRun',
    title: 'Rechnungslauf',
    component: () => import('@components/InvoiceRun/InvoiceRunSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'addOfficeLocation',
    title: 'Standort erstellen',
    component: () =>
      import('@components/OfficeLocations/AddOfficeLocationsModal/AddOfficeLocationsModal.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Standorte workspace (ADR-0027 §A — "creating a record is a Column"). The
    // prefill-free create opens as a root cascade column on /office/subscriptions
    // via the `new` sentinel; the sidebar is create-only, so `officeLocationDraftId`
    // is a column-identity token it ignores. Mirrors createPatient/inviteUser. The
    // SuperAdmin opener omits the id and stays an overlay (non-workspace route).
    inline: {
      idProp: 'officeLocationDraftId',
    },
  },
  {
    type: 'exerciseProgramStatistic', //
    title: 'Statistiken',
    component: () => import('@components/ExerciseExecution/ProgramStatistic/ProgramStatistic.vue'),
    noPadding: false,
    hideButtons: true,
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'exerciseProgramDetail', //
    title: 'Programmdetails',
    component: () => import('@components/ExerciseExecution/ExerciseProgramDetailModal.vue'),
    closeWithClickOutside: true,
    noPadding: true,
    hideButtons: true,
    position: 'right',
  },
  {
    type: 'appointmentDetails', //
    title: 'Termindetails',
    component: () => import('@components/WaitingRoom/AppointmentDetails/AppointmentDetails.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // View/edit-by-`appointmentId` is inline-capable (RS-005). The create path
    // (no `appointmentId`) stays overlay — `shouldRouteToCascade` falls through
    // to overlay when the registered idProp is absent from the open props.
    // RS-007: docks under patientFile, pins it to GENERAL (appointment list lives
    // there). The parentIds idProp binding (`patientId`) is harmless extra attr.
    inline: {
      idProp: 'appointmentId',
      parentIds: [{ type: 'patientFile', idProp: 'patientId' }],
      parentTab: PatientFileMark.GENERAL,
    },
  },
  {
    // The appointment panel as a cascade column (ADR-0046 — the frame follows the host):
    // the same body the calendar's peek shows, framed as a column on workspace
    // routes. Offices without APPOINTMENT_SCHEDULING still open `appointmentDetails`.
    // Docks under `patientFile` in its appointments slot and pins it to GENERAL, where
    // the appointment list lives; the `patientId` binding is a harmless extra attr.
    // A calendar card opens it as the root column beside the calendar.
    type: 'appointment',
    title: 'Termindetails',
    component: () => import('@components/WaitingRoom/AppointmentDetails/AppointmentColumn.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // The create side's step „Planung" shows the week view (#1941) and needs the room;
    // the detail side does not.
    wide: 'create',
    inline: {
      idProp: 'appointmentId',
      parentIds: [{ type: 'patientFile', idProp: 'patientId' }],
      parentTab: PatientFileMark.GENERAL,
    },
  },
  {
    // Planning a Verordnung's open units (#1938): the proposals as a list, booked in
    // one go. Opened by „Termine planen" on the Verordnung, it docks under that
    // `prescription` column; „Planen" at an appointment plans in the appointment's own
    // column instead (ADR-0048). The bindings are harmless extra attrs. `wide`: the
    // calendar view of the planning (#1941) needs the room.
    type: 'appointmentPlanning',
    title: 'Termine planen',
    component: () => import('@components/AppointmentPlanning/AppointmentPlanningColumn.vue'),
    closeWithClickOutside: true,
    position: 'right',
    wide: true,
    inline: {
      idProp: 'treatmentPlanId',
      parentIds: [
        { type: 'prescription', idProp: '_referrerTreatmentPlanId' },
        { type: 'appointment', idProp: '_referrerAppointmentId' },
      ],
    },
  },
  {
    type: 'appointmentComposer', //
    title: 'Termin erstellen',
    component: () => import('@components/AppointmentComposer/AppointmentComposer.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // A prefill-free compose opens as a top-level cascade column on a workspace
    // route (calendar). The id is always the `new` sentinel — the composer is
    // create-only, so `composerId` is a column-identity token it ignores.
    // ADR-0027 §A: when opened from inside a patient (patient-only prefill), the
    // create-Column docks under `patientFile` and binds the patient's id to
    // `initialPatientId`; when opened from a treatment-plan task it docks under
    // the plan's `prescription` column and binds the plan id to
    // `initialTreatmentPlanId`. In both cases the seed is recoverable on a cold
    // deep-link — the same shape as `prescription`'s `:new` draft (US5). Routing
    // picks the DEEPEST matching parent (prescription nests under patientFile), so drilling from a
    // plan lands under the plan. Off any match (e.g. the calendar Termin-erstellen
    // button) it opens at root as before.
    inline: {
      idProp: 'composerId',
      parentIds: [
        // A saved plan's Verordnung column (TM-9664: its only column — the read view
        // is gone) or the one „Speichern und Termine planen" was pressed in: the
        // composer docks under it and binds its plan from it, which is what lets a
        // cold deep-link of this flow rebuild itself.
        { type: 'prescription', idProp: 'initialTreatmentPlanId' },
        { type: 'patientFile', idProp: 'initialPatientId' },
      ],
      parentTab: PatientFileMark.GENERAL,
    },
  },
  {
    type: 'createPatient',
    title: 'Patient anlegen',
    component: () => import('@components/PatientAccounts/PatientCreateColumnPanel.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Opened stacked on top of the appointment panel's peek (RS-005 / #949) — must
    // render above it, not behind.
    elevated: true,
    // Prefill-free create opens as a cascade column on a workspace route (the patient
    // list). The id is always the `new` sentinel — the sidebar is create-only, so
    // `patientDraftId` is a column-identity token it ignores. Contextual openers
    // (dashboard, waiting room, appointment panel) omit the id and stay overlays;
    // the created id returns via `onSidebarClose` either way.
    //
    // `parentIds` names the create flows whose patient picker can open this one (the
    // "+" in PatientDropdown). Docking under them rather than at root is what keeps
    // the half-written Termin or Verordnung on the board — a root open clears the tree
    // (ADR-0027 §C-3), and with it the picker that is waiting for the new patient.
    // Nothing else is open above it on the patient list, so it still lands at root
    // there, as before.
    inline: {
      idProp: 'patientDraftId',
      parentIds: [
        { type: 'appointmentComposer', idProp: 'openedFromId' },
        { type: 'prescription', idProp: 'openedFromId' },
        { type: 'basket', idProp: 'openedFromId' },
        { type: 'addExercise', idProp: 'openedFromId' },
      ],
    },
  },
  {
    type: 'inviteUser',
    title: 'Personal einladen',
    component: () => import('@components/OfficeMgm/InviteUserSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Mirrors createPatient: can be opened stacked on top of a peek, so it must
    // render above the peek band.
    elevated: true,
    // Mirrors createPatient: the Personal-tab "Personal einladen" button opens a
    // root cascade column via the `new` sentinel; opening from inside another
    // overlay (AddOfficeLocationsModal) omits the id and stays an overlay.
    inline: {
      idProp: 'staffDraftId',
    },
  },
  {
    type: 'customFormDetail', //
    title: 'Formular bearbeiten',
    component: () => import('@components/Settings/CustomForm/CustomFormEditor.vue'),
    closeWithClickOutside: false,
    position: 'right',
    // CustomForm workspace (#1111/#1120): the editor docks as a ROOT cascade
    // column, opened from the forms list with `openAtRoot` (re-roots, clears the
    // board). No `parentIds` — like `template`, it opens at root; off a workspace
    // route it stays an overlay. The `:new` id is the create-draft column. The
    // The discard/dirty guard is hand-rolled in the view so a clean close never
    // prompts.
    inline: {
      idProp: 'customFormId',
    },
  },
  {
    type: 'previewCustomForm', //
    title: 'Formular-Vorschau',
    component: () => import('@components/Settings/CustomForm/PreviewCustomForm.vue'),
    closeWithClickOutside: true,
    // No discard-confirm on close: the preview is a read-only render of the
    // editor's draft and owns no state of its own, so there is nothing to lose.
    // (Once it moved onto GenericSidebarTemplate the shared ✕ started honouring
    // this flag, which turned every dismissal into a spurious "verwerfen?" prompt
    // — the hand-rolled button it replaced always closed straight away.)
    position: 'right',
    // CustomForm workspace (#1111/#1120): the preview docks as a CHILD column of
    // the editor, opened on-demand by the "Vorschau" button and closed by its own
    // ✕. It bends the ADR "column id = its own record" rule by reusing the form's
    // id purely for URL identity — mechanically safe because the distinct `type`
    // keeps `{previewCustomForm, id}` from colliding with `{customFormDetail, id}`
    // on re-trigger dedup. The component renders the live editor draft, not this id.
    inline: {
      idProp: 'customFormId',
      parentIds: [{ type: 'customFormDetail', idProp: 'customFormId' }],
      closesWithParent: true,
    },
  },
  {
    type: 'basket', //
    title: 'Übungsprogramm',
    component: () => import('@components/ExerciseLibrary/BasketModal/BasketModal.vue'),
    closeWithClickOutside: true,
    hideButtons: true,
    noPadding: false,
    position: 'right',
    // Übungsbibliothek workspace (ADR-0027 §A): "Übungen zuweisen" opens the
    // program basket as a top-level cascade column (mirrors createPatient). The
    // id is always the `new` sentinel — the basket has no record id, so
    // `basketDraftId` is a column-identity token; content lives in
    // `exerciseBasketStore`. No `parentIds` — opens at root from the library.
    // Contextual openers (patient-file program replace) omit the id and stay
    // overlays; off a workspace route it stays an overlay too. A cold reload of
    // `basket:new` rebuilds the COLUMN but not its contents: the basket is in-memory
    // only. It used to persist to sessionStorage, which made a reloaded tab show a
    // count badge on a basket nobody had reopened.
    //
    // NOT an Entwurf (ADR-0029 §H-3): it holds no buffer, gets no rail entry, no menu
    // dot and no close guard — closing the column leaves the Korb full, so there is
    // nothing a discard prompt could be about (§H-3-bis).
    inline: {
      idProp: 'basketDraftId',
    },
  },
  {
    type: 'template', //
    title: 'Vorlage bearbeiten',
    component: () => import('@components/ExerciseLibrary/TemplateModal/TemplateModal.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Übungsbibliothek workspace (ADR-0027 §A): a Template is a record with an
    // id, so it docks as a cascade column. The panel already self-fetches by
    // `exerciseTemplateId` (exerciseProgramsQueries.getById), so it hydrates on a
    // cold inline deep-link. No `parentIds` — like `exerciseDetails`, it opens at
    // root from the library list; off a workspace route it stays an overlay.
    inline: {
      idProp: 'exerciseTemplateId',
    },
  },
  {
    type: 'addExercise', //
    title: 'Übung erstellen',
    component: () => import('@components/ExerciseLibrary/AddExerciseModal/AddExerciseModal.vue'),
    hasScrollToBottomButton: true,
    closeWithClickOutside: true,
    position: 'right',
    // Übungsbibliothek workspace (ADR-0027 §A): a prefill-free "Übung erstellen"
    // opens as a top-level cascade column (mirrors createPatient). The id is the
    // `new` sentinel and its later generations — the modal is create-only, so
    // `addExerciseDraftId` is a column-identity token it never reads as data, but its
    // presence is what marks this a draft column rather than a contextual overlay
    // (ADR-0029 §H-1). The panel takes a fresh wizard from the draft seam when there
    // is nothing to restore, so it hydrates cleanly on a cold inline deep-link. No
    // `parentIds` — it opens at root from the library. Contextual/prefilled openers
    // (patient-scoped from ExerciseLibraryCore/ItemsEditor, QR flows) omit the id and
    // stay overlays; off a workspace route it stays an overlay too.
    // Opened from the patient file's "Übung zum Programm hinzufügen" column, it docks
    // under that column (see `programId` above); everywhere else there is no such
    // column and it opens at root as before.
    inline: {
      idProp: 'addExerciseDraftId',
      parentIds: [{ type: 'exerciseLibrary', idProp: 'programId' }],
    },
  },
  {
    type: 'staffFile', //
    title: 'Mitarbeiterprofil',
    component: () => import('@components/StaffFile/StaffFile.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // The Personal (staff) list is a columnar workspace: opening a staff member
    // lands as a root cascade column keyed by `staffId`. Tabs (Arbeitszeit /
    // Zertifikate) are role-gated — resolved reactively in
    // `resolveInlineTabItems` from the fetched staff — so `defaultTabView` only
    // fixes the cold deep-link tab. No `parentIds`: staff opens at root.
    inline: {
      idProp: 'staffId',
      defaultTabView: StaffFileSidebarViewEnum.GENERAL,
    },
  },
  {
    type: 'officeAchievements', // Not a sidebar
    title: 'Praxis Challenge',
    component: () => import('@components/OfficeAchievement/OfficeAchievementSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'feedback', // NOT a sidebar
    title: 'Feedback',
    component: () => import('@components/AppFeedback/AppFeedbackModal.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'patientFilePreview', //
    title: 'Patientenakte',
    component: () => import('@components/PatientFile/PatientFilePreview.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'patientFile', //
    title: 'Patientenakte',
    component: () => import('@components/PatientFile/PatientFileSidebar.vue'),
    closeWithClickOutside: true,
    noPadding: false,
    position: 'right',
    // Patient cascade (RS-007): patientFile docks under appointmentDetails for
    // the "open a different patient from inside an appointment" drill
    // (US7), and under prescription / patientInvoiceDetail for the
    // "open the patient from a prescription / invoice" drills off the global
    // Verordnungen and Rechnungen lists — where that detail column is the root
    // (or the deepest context) and the patient opens as its child. When opened
    // from the Patient:innen list (cascade empty) it lands at root; from
    // anywhere outside a workspace it stays an overlay.
    // The `_referrer*` bindings are structural — patientFile doesn't consume
    // them; they just satisfy the registry's idProp contract.
    inline: {
      idProp: 'patientId',
      defaultTabView: PatientFileMark.GENERAL,
      parentIds: [
        { type: 'appointmentDetails', idProp: '_referrerAppointmentId' },
        { type: 'appointment', idProp: '_referrerAppointmentId' },
        { type: 'prescription', idProp: '_referrerTreatmentPlanId' },
        { type: 'patientInvoiceDetail', idProp: '_referrerInvoiceId' },
      ],
    },
  },
  {
    type: 'patientNotificationSettings',
    title: 'Benachrichtigungen',
    component: () => import('@components/PatientFile/PatientNotificationSettings.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Opened from the patient file's Funktionen menu (TM-9529), so it docks under
    // `patientFile` and its own id IS the patient id — a cold deep-link needs
    // nothing but the segment to fetch the right settings. The `_referrerPatientId`
    // binding is the usual harmless extra attr; it exists only to name the parent
    // type for cascade insertion.
    inline: {
      idProp: 'patientId',
      parentIds: [{ type: 'patientFile', idProp: '_referrerPatientId' }],
    },
  },
  {
    type: 'aiDictation',
    title: 'Diktat',
    component: () => import('@components/PatientFile/AiDocumentation/DictationColumn.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // KI-Dokumentation (TM-9652): processing, review and failure of one dictation,
    // opened from its appointment card in the Dokumentations-Tab. The panel
    // self-fetches by `dictationId` and derives its stage from the server, so a
    // cold deep-link shows whatever the dictation is at now. Shares the tab's
    // `documentation` slot with the provenance column.
    inline: {
      idProp: 'dictationId',
      parentIds: [{ type: 'patientFile', idProp: 'patientId' }],
      parentTab: PatientFileMark.DOCUMENTATION,
    },
  },
  {
    type: 'aiTranscript',
    title: 'Transkript',
    component: () => import('@components/PatientFile/AiDocumentation/TranscriptColumn.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // The transcript beside the review it is checked against (TM-9652): a view of
    // its parent dictation, so minimizing that column closes this one too.
    inline: {
      idProp: 'dictationId',
      parentIds: [{ type: 'aiDictation', idProp: '_referrerDictationId' }],
      closesWithParent: true,
    },
  },
  {
    type: 'aiDocumentationProvenance',
    title: 'Herkunft des KI-Entwurfs',
    component: () => import('@components/PatientFile/AiDocumentation/ProvenanceColumn.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // „Provenienz anzeigen" on a saved AI_ASSISTED entry (TM-9594), a column since
    // TM-9652: read-only, self-fetched by `itemId`, in the Dokumentations-Tab's
    // `documentation` slot next to the entry it explains.
    inline: {
      idProp: 'itemId',
      parentIds: [{ type: 'patientFile', idProp: 'patientId' }],
      parentTab: PatientFileMark.DOCUMENTATION,
    },
  },
  {
    type: 'exerciseLibrary', //
    title: 'Übung zum Programm hinzufügen',
    component: () => import('@components/ExerciseLibrary/ExerciseLibrarySidebar.vue'),
    closeWithClickOutside: true,
    noPadding: false,
    hideButtons: true,
    position: 'right',
    // Patient cascade: docks as a child column of the `programDetail` whose items
    // are being edited, keyed by that program's id. It works on the ItemsEditor's
    // unsaved draft, so the editor closes it when editing ends — and a cold
    // deep-link, which has no editor in edit mode, closes it straight away.
    inline: {
      idProp: 'programId',
      parentIds: [{ type: 'programDetail', idProp: '_parentProgramId' }],
    },
  },
  {
    type: 'library', // NOT a sidebar
    title: 'Bibliothek',
    component: () => import('@components/ExerciseLibrary/LibrarySideList/LibrarySideList.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'officeProductDetails', //
    title: 'Leistung',
    component: () => import('@components/OfficeShop/OfficeProductDetails/OfficeProductColumn.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Root column of the shop workspace: the products list is the page-level root
    // list, so no `parentIds`. The panel self-fetches the product by id (and the
    // Stripe account by current office location), so it hydrates on a cold
    // deep-link; `officeProductId` may be the `NEW_ID_SENTINEL` draft marker.
    inline: {
      idProp: 'officeProductId',
    },
  },
  {
    type: 'officeShopAccount', //
    title: 'Konto & Steuern',
    component: () => import('@components/OfficeShop/OfficeShopAccountSidebar.vue'),
    closeWithClickOutside: true,
    hideButtons: true,
    position: 'right',
    // Transient mobile-only panel for chrome that is inline on desktop — never a
    // cascade column, or it would compete with officeProductDetails for the
    // shop's root column slot.
    alwaysOverlay: true,
  },
  {
    type: 'productDetails', //
    title: 'Produktdetails',
    component: () => import('@components/OfficeShop/PatientShop/ProductDetailsSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'featureRanking', //
    title: 'Feature Ranking',
    component: () => import('@components/FeatureRanking/FeatureRankingModal.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'exerciseExecutionPlayerSettings', //
    title: 'Einstellungen',
    component: () =>
      import('@components/ExerciseExecution/Player/ExerciseExecutionPlayerSettingsSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'versandLieferungDetail',
    title: 'Lieferung',
    component: () => import('@components/Versand/VersandLieferungDetailSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Versand console (ADR-0027 §A): a Lieferung is a record. The column fetches
    // its own data via `lieferungId` and opens at the root out of the Lieferungen
    // table — so a cold deep link out of a ticket hydrates without the list.
    inline: {
      idProp: 'lieferungId',
    },
  },
  {
    type: 'webhookSubscriptionDetails', //
    title: 'Webhook-Endpunkt',
    component: () =>
      import('@components/DeveloperSettings/Webhooks/WebhookSubscriptionSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Webhooks workspace (ADR-0027 §A): a WebhookSubscription is a record. The
    // panel self-fetches by id (webhooksQueries.getSubscriptionById) and opens at
    // root from the subscription table. The create open (id absent) falls through
    // to overlay; off a workspace route it stays an overlay.
    inline: {
      idProp: 'webhookSubscriptionId',
    },
  },
  {
    type: 'webhookSubscriptionLogs', //
    title: 'Webhook-Logs',
    component: () =>
      import('@components/DeveloperSettings/Webhooks/WebhookSubscriptionLogsSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    noPadding: false,
    hideButtons: true,
    // Webhooks workspace (ADR-0027 §A): the per-subscription log list, keyed by
    // `webhookSubscriptionId`. Self-fetches the subscription by id and drives its
    // log table from the same id, so it hydrates on a cold deep-link. Opens at
    // root from the subscription table's row action.
    inline: {
      idProp: 'webhookSubscriptionId',
    },
  },
  {
    type: 'webhookLogEntryDetails', //
    title: 'Log-Eintrag',
    component: () =>
      import('@components/DeveloperSettings/Webhooks/WebhookLogEntryDetailsSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Webhooks workspace (ADR-0027 §A): a single WebhookLogEntry record. Opened
    // from inside the `webhookSubscriptionLogs` column, so it docks as that
    // column's child. Self-fetches by `webhookLogEntryId` (getLogById). The
    // parentIds idProp binding (`webhookSubscriptionId`) is a harmless extra attr
    // the panel ignores.
    inline: {
      idProp: 'webhookLogEntryId',
      parentIds: [{ type: 'webhookSubscriptionLogs', idProp: 'webhookSubscriptionId' }],
    },
  },
  {
    type: 'apiKeyDetails', //
    title: 'API-Key',
    component: () => import('@components/DeveloperSettings/ApiKeys/ApiKeySidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // API-Keys workspace (ADR-0027 §A): an ApiKey is a record. The panel
    // self-fetches by id (apiKeysQueries.getById) and opens at root from the
    // key table. The create open (id absent) falls through to overlay; off a
    // workspace route it stays an overlay.
    inline: {
      idProp: 'apiKeyId',
    },
  },
  {
    type: 'anamnesisResults', //
    title: 'Anamnese-Ergebnisse',
    component: () =>
      import('@components/PatientFile/Diagnosis/Anamnesis/PatientFileDiagnosisAnamnesisResults.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Patient cascade (RS-007): docks under patientFile, pinned to the DIAGNOSIS
    // (Befund) tab, where the anamnesis/custom-form answer list lives. The panel
    // self-fetches by `customFormAnswerId`, so it hydrates on a cold deep-link.
    // The parentIds idProp binding (`patientId`) is a harmless extra attr.
    inline: {
      idProp: 'customFormAnswerId',
      parentIds: [{ type: 'patientFile', idProp: 'patientId' }],
      parentTab: PatientFileMark.DIAGNOSIS,
    },
  },
  {
    type: 'customFormPlayer',
    title: 'Fragebogen',
    component: () => import('@components/PatientFile/Diagnosis/Anamnesis/CustomFormPlayer.vue'),
    closeWithClickOutside: false,
    position: 'right',
    // Same docking as anamnesisResults (RS-007): under patientFile, pinned to
    // the DIAGNOSIS (Befund) tab, where forms are filled out from. The panel
    // self-fetches by `customFormId`, so it hydrates on a cold deep-link.
    // `inline` only ever fires on a workspace route (shouldRouteToCascade,
    // sidebarStore.ts); the patient's own `/diagnosis` isn't one (TM-9295 PR6
    // — patients get sidebars, never columns), so opening this there always
    // resolves to the overlay. The therapist's PatientFile IS a workspace
    // route, where the same registration docks it as a column instead.
    inline: {
      idProp: 'customFormId',
      parentIds: [{ type: 'patientFile', idProp: 'patientId' }],
      parentTab: PatientFileMark.DIAGNOSIS,
    },
  },
  {
    type: 'programDetail',
    title: 'Programmdetails',
    component: () => import('@components/PatientFile/Program/ProgramDetailSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    // Patient cascade (RS-007): docks under patientFile, pinned to the PROGRAM
    // tab, which since the Programm/Statistiken merge holds both the program
    // list and the statistics below it. The panel self-fetches by `programId`, so
    // it hydrates on a cold deep-link. The parentIds idProp binding (`patientId`)
    // is a harmless extra attr the panel ignores.
    inline: {
      idProp: 'programId',
      parentIds: [{ type: 'patientFile', idProp: 'patientId' }],
      noSwapRing: true,
      parentTab: PatientFileMark.PROGRAM,
    },
  },
  {
    type: 'priceListDraftEditor',
    title: 'Preisliste bearbeiten',
    component: () => import('@components/PriceLists/PriceListDraftEditor.vue'),
    closeWithClickOutside: true,
    position: 'right',
    inline: {
      idProp: 'priceListId',
      parentTab: OfficeLocationSidebarViewEnum.PRICE_LISTS,
      parentIds: [{ type: 'officeLocationSettings', idProp: 'officeLocationId' }],
    },
  },
  {
    type: 'priceListDetails',
    title: 'Preisliste',
    component: () => import('@components/PriceLists/PriceListDetailsSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    inline: {
      idProp: 'priceListId',
      defaultTabView: 'details',
      parentTab: OfficeLocationSidebarViewEnum.PRICE_LISTS,
      parentIds: [{ type: 'officeLocationSettings', idProp: 'officeLocationId' }],
    },
  },
  {
    type: 'priceListItemEdit',
    title: 'Position bearbeiten',
    component: () => import('@components/PriceLists/PriceListItemEditSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
    inline: {
      idProp: 'itemId',
      parentTab: 'positionen',
      parentIds: [
        { type: 'priceListDetails', idProp: 'priceListId' },
        { type: 'officeLocationSettings', idProp: 'officeLocationId' },
      ],
    },
  },
  {
    // Overlay-only (ADR-0027 §A): no record exists until the practice confirms.
    type: 'patientNewEpisode',
    title: 'Termin buchen',
    component: () => import('@components/AppointmentOverview/PatientNewEpisodeSidebar.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'videoCallInfo',
    title: 'Videosprechstunde',
    component: () => import('@components/VideoCall/VideoCallInfoSidebar.vue'),
    closeWithClickOutside: true,
    hideButtons: true,
    position: 'right',
  },
  {
    type: 'settingAccounts',
    title: 'Kontoeinstellungen',
    component: () => import('@views/SettingsAccountView.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'officeProfile',
    title: 'Einstellungen',
    component: () => import('@views/AccountSettingsView.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'settingNotifications',
    title: 'Benachrichtigungen',
    component: () => import('@views/SettingsNotificationView.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'help',
    title: 'Hilfe',
    component: () => import('@views/Help.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'settingsLegal',
    title: 'Rechtliches',
    component: () => import('@views/SettingsLegalView.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
  {
    type: 'settingsApiKeys',
    title: 'API-Keys',
    component: () => import('@components/DeveloperSettings/ApiKeys/ApiKeysOverview.vue'),
    closeWithClickOutside: true,
    position: 'right',
  },
] as const;

/**
 * Which prop a type's cascade column carries its own id in, when it can be a column
 * at all.
 *
 * The lookup was written out at eight sites, each of which then had to remember that
 * a type may have no `inline` capability. It is the one question every caller asks of
 * `inline` from outside the cascade — which draft a panel's props name, which column a
 * close should target — so it is answered here once.
 */
export function idPropOf(type: string | undefined): string | undefined {
  if (!type) return undefined;
  return sidebars.find((sidebar) => sidebar.type === type)?.inline?.idProp;
}

/**
 * What a cascade column id says an opening IS (TM-9455 / ADR-0029 §C-quinquies).
 *
 * - `create` — the `:new` sentinel, or a later generation of it (`new~2`). The one
 *   active create column.
 * - `record` — a real id: an edit of something saved.
 * - `unbound` — no id at all. A contextual overlay carries none, and neither does a
 *   column in the beat before the cascade binds one.
 */
export type ColumnIdKind = 'create' | 'record' | 'unbound';

/**
 * Read an opening's own id — see {@link ColumnIdKind}. Which prop holds it is
 * {@link idPropOf}'s question.
 *
 * Three values rather than a boolean because the third case is real and the two callers
 * answer it DIFFERENTLY, on purpose. The draft seam reads `unbound` as "not a create
 * column", so a contextual open gets a throwaway buffer rather than the active Entwurf's.
 * The draft registry reads it as "not a saved record", so a column names no identity and
 * takes the create-side title until its id lands.
 *
 * Written out per site, that difference is the drift ADR-0029 §H opens with: the same
 * question asked with `!props.x?.id` in one place and the sentinel test in another, and
 * an edit opened before its prop arrived read as a create draft in one panel and as an
 * overlay everywhere else. Nothing is obviously wrong at any single site, which is why
 * the question is answered once, here, and the third case cannot be answered by accident.
 */
export function idKindOf(id: unknown): ColumnIdKind {
  if (typeof id !== 'string' || id.length === 0) return 'unbound';
  return isDraftColumnId(id) ? 'create' : 'record';
}
